import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

type Cell = boolean | string;
type Row = { label: string; cells: [Cell, Cell, Cell, Cell] };

const cols = ["SendFeedback.ai", "Culture Amp", "Lattice", "Officevibe"];

const rows: Row[] = [
  { label: "Truly anonymous (PII stripped at DB level)", cells: [true, "Partial", "Partial", "Partial"] },
  { label: "Conversational AI follow-ups", cells: [true, false, false, false] },
  { label: "Auto theme & sentiment clustering", cells: [true, true, "Limited", false] },
  { label: "Retention risk scoring", cells: [true, "Add-on", true, false] },
  { label: "Always-on, not just quarterly", cells: [true, false, false, true] },
  { label: "Setup in under 5 minutes", cells: [true, false, false, true] },
  { label: "Flat pricing (no per-seat fees)", cells: [true, false, false, false] },
  { label: "Built for HR (not engineering)", cells: [true, true, "Mixed", true] },
];

const Mark = ({ v, className }: { v: Cell; className?: string }) => {
  if (v === true)
    return <Check className={cn("h-5 w-5 text-[hsl(var(--mkt-primary))] mx-auto", className)} aria-label="Yes" />;
  if (v === false)
    return <X className={cn("h-5 w-5 text-muted-foreground/50 mx-auto", className)} aria-label="No" />;
  return <span className="text-xs sm:text-sm text-muted-foreground">{v}</span>;
};

export const ComparisonTable = () => {
  return (
    <section className="container mx-auto px-3 lg:px-8 py-12 sm:py-16 lg:py-24">
      <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
        <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--mkt-primary))] mb-3">
          Why not just use…
        </div>
        <h2 className="text-3xl sm:text-5xl lg:text-6xl tracking-tight">
          The Honest Comparison
        </h2>
        <p className="mt-3 sm:mt-4 text-sm sm:text-base text-muted-foreground">
          Here's what you actually get vs. the messy workarounds most HR teams cobble together.
        </p>
      </div>

      {/* Mobile: stacked per-competitor cards */}
      <div className="md:hidden max-w-md mx-auto space-y-4">
        {cols.slice(1).map((competitor, compIdx) => (
          <div
            key={competitor}
            className="rounded-3xl bg-card border border-border mkt-shadow-card overflow-hidden"
          >
            <div className="grid grid-cols-2 bg-muted/60 text-center">
              <div className="px-3 py-3 text-xs font-bold text-[hsl(var(--mkt-primary-deep))] border-r border-border">
                SendFeedback.ai
              </div>
              <div className="px-3 py-3 text-xs font-bold text-foreground/70">
                {competitor}
              </div>
            </div>
            {rows.map((r, idx) => (
              <div
                key={r.label}
                className={cn(
                  "px-4 pt-3 pb-2",
                  idx !== rows.length - 1 && "border-b border-border"
                )}
              >
                <div className="text-[13px] font-medium text-foreground mb-2 leading-snug text-center">
                  {r.label}
                </div>
                <div className="grid grid-cols-2 items-center pb-1">
                  <div className="flex justify-center border-r border-border/60 py-1">
                    <Mark v={r.cells[0]} />
                  </div>
                  <div className="flex justify-center py-1">
                    <Mark v={r.cells[compIdx + 1]} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Desktop/tablet: full grid */}
      <div className="hidden md:block max-w-5xl mx-auto rounded-3xl bg-card border border-border mkt-shadow-card overflow-hidden">
        <div className="grid grid-cols-5 bg-muted/60">
          <div className="px-6 py-5 text-sm font-semibold text-muted-foreground">Capability</div>
          {cols.map((c, i) => (
            <div
              key={c}
              className={cn(
                "px-6 py-5 text-center text-sm font-bold",
                i === 0 ? "text-[hsl(var(--mkt-primary-deep))]" : "text-foreground/70"
              )}
            >
              {c}
            </div>
          ))}
        </div>
        {rows.map((r, idx) => (
          <div
            key={r.label}
            className={cn(
              "grid grid-cols-5 items-center",
              idx !== rows.length - 1 && "border-b border-border"
            )}
          >
            <div className="px-6 py-4 text-sm font-medium text-foreground">{r.label}</div>
            {r.cells.map((c, i) => (
              <div
                key={i}
                className={cn(
                  "px-6 py-4 text-center",
                  i === 0 && "bg-[hsl(var(--mkt-primary)/0.04)]"
                )}
              >
                <Mark v={c} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};
